import {
  Card,
  CardContent,
  Stack,
  Typography,
  Divider,
  CircularProgress,
  Box,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ApexChart from 'react-apexcharts';
import { Device } from '../../../store/reducers/deviceReducers';
import deviceApi from '../../../api/devices';

const DeviceReadings = ({ device, refreshKey = 0 }: { device: Device; refreshKey?: number }) => {
  const [readings, setReadings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    const fetchReadings = async () => {
      try {
        setLoading(true);
        // Use serial as device_id (sensors are created with serial as device_id)
        const deviceId = device.serial;
        const response = await deviceApi.fetchDeviceReadingsLast10Days(deviceId)();
        setReadings(response.data || []);
      } catch (error) {
        console.error('Error fetching device readings:', error);
        setReadings([]);
      } finally {
        setLoading(false);
      }
    };

    if (device.serial) {
      fetchReadings();
    }
  }, [device.serial, refreshKey]);

  const formatYAxisValue = (value: number | null) => {
    if (value == null) {
      return '';
    }
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  };

  const sortedReadings = readings
    .map((reading) => ({
      x: new Date(reading.timestamp).getTime(),
      y: reading.value,
    }))
    .sort((a, b) => a.x - b.x);

  // Calculate y-axis bounds with proper padding
  const yAxisValues = sortedReadings.map((r) => r.y).filter((v) => v != null && !isNaN(v));
  let yAxisMin = 0;
  let yAxisMax = 100;

  if (yAxisValues.length > 0) {
    const dataMin = Math.min(...yAxisValues);
    const dataMax = Math.max(...yAxisValues);
    const range = dataMax - dataMin;
    const padding = range * 0.4; // 40% padding on each side
    yAxisMin = dataMin - padding;
    yAxisMax = dataMax + padding;

    // Ensure we have some reasonable bounds
    if (yAxisMin === yAxisMax) {
      yAxisMin = yAxisMin - 1;
      yAxisMax = yAxisMax + 1;
    }
  }

  const chartOptions = {
    chart: {
      type: 'line' as const,
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      stacked: false,
    },
    redrawOnParentResize: true,
    colors: [theme.palette.primary.main],
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    stroke: {
      width: 2,
      curve: 'straight' as const,
    },
    grid: {
      borderColor: '#e7e7e7',
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5,
      },
    },
    markers: {
      colors: [theme.palette.primary.main],
      strokeColors: [theme.palette.primary.main],
    },
    xaxis: {
      type: 'datetime' as const,
      tickAmount: 5,
      tickPlacement: 'between' as const,
      labels: {
        rotate: 0,
        hideOverlappingLabels: true,
        format: 'dd MMM',
        datetimeUTC: false,
      },
      title: {
        text: 'Timestamp',
      },
    },
    yaxis: {
      labels: {
        formatter: formatYAxisValue,
      },
      title: {
        text: 'Reading',
      },
      min: yAxisMin,
      max: yAxisMax,
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: formatYAxisValue,
      },
    },
  };

  const chartSeries = [
    {
      name: 'Reading Value',
      data: sortedReadings.map((r) => [r.x, r.y]),
    },
  ];

  return (
    <Card elevation={2} sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={3}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <ShowChartIcon color='primary' />
            <Typography variant='h6'>Recent Readings (Last 10 Days)</Typography>
          </Stack>
          <Divider />
          {loading ? (
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='300px'>
              <CircularProgress />
            </Box>
          ) : readings.length === 0 ? (
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='300px'>
              <Typography variant='body2' color='text.secondary' align='center'>
                No readings available for this device
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: 300 }}>
              <ApexChart
                height='100%'
                width='100%'
                options={chartOptions}
                series={chartSeries}
                type='line'
              />
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DeviceReadings;
